/**
 * The meetup schedule, computed, so the site never needs a date bump.
 *
 * Cadence: every other Thursday, 6 to 8 PM Dallas time, at Lola's. ANCHOR is a
 * Thursday the meetup ran on; the next ones are 14-day steps from it. A night
 * that is called off goes in SKIPPED as an ISO date and the site moves on to
 * the one after. ANCHOR changes only if the cadence itself shifts; the venue
 * and the hours live with the copy that names them.
 *
 * A meetup counts as "next" until it ends: on the night itself the site still
 * says tonight's date until 8 PM Dallas time, then rolls to the following one.
 */
export const ANCHOR = '2026-10-22';
export const SKIPPED: string[] = [];
export const START_HOUR = 18;
export const END_HOUR = 20;
export const TIME_ZONE = 'America/Chicago';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type Clock = { date: string; hour: number };

/** The calendar date (ISO) and hour it is right now in Dallas. */
function dallasClock(now: Date): Clock {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: TIME_ZONE,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
  }).formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    hour: Number(get('hour')) % 24,
  };
}

/** Dates are walked as UTC midnights so the arithmetic is pure calendar math. */
function toIso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function isOver(iso: string, clock: Clock): boolean {
  return iso < clock.date || (iso === clock.date && clock.hour >= END_HOUR);
}

/**
 * The next `count` meetup dates as ISO strings (YYYY-MM-DD), earliest first,
 * as of `now`. `skipped` defaults to the SKIPPED list above.
 */
export function upcomingMeetups(count = 2, now: Date = new Date(), skipped: string[] = SKIPPED): string[] {
  const clock = dallasClock(now);
  const cursor = new Date(`${ANCHOR}T00:00:00Z`);
  const out: string[] = [];
  while (out.length < count) {
    const iso = toIso(cursor);
    if (!isOver(iso, clock) && !skipped.includes(iso)) out.push(iso);
    cursor.setUTCDate(cursor.getUTCDate() + 14);
  }
  return out;
}

/** 'Thu Nov 5', the house short form (BRAND.md). */
export function shortDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return `${WEEKDAYS[d.getUTCDay()]} ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

/** The `dates=` value for a Google Calendar template link, 6 to 8 PM local. */
export function gcalDates(iso: string): string {
  const day = iso.replace(/-/g, '');
  const hh = (h: number) => `${String(h).padStart(2, '0')}0000`;
  return `${day}T${hh(START_HOUR)}/${day}T${hh(END_HOUR)}`;
}

/** The Google Calendar link for the series, anchored on the next meetup. */
export function gcalUrl(iso: string): string {
  return (
    'https://calendar.google.com/calendar/render?action=TEMPLATE' +
    '&text=Oak%20Cliff%20Tech%20Meetup' +
    `&dates=${gcalDates(iso)}&ctz=${TIME_ZONE.replace('/', '%2F')}` +
    '&recur=RRULE%3AFREQ%3DWEEKLY%3BINTERVAL%3D2%3BBYDAY%3DTH' +
    '&location=Lola%27s%20Cantina%20%28at%20East%20Dock%29%2C%20900%20E.%20Clarendon%20Dr%2C%20Dallas%2C%20TX%2075203' +
    '&details=Show%20%26%20tell%20mixer%20%E2%80%94%20bring%20something%20you%27re%20working%20on.%20https%3A%2F%2Foakcliff.tech%2Fmeetup'
  );
}
